import { Link } from "react-router-dom";
import {
  TARGET_BUILDERS,
  type Blueprint,
  type BlueprintStatus,
  type TargetBuilder,
} from "@/features/blueprints/blueprintTypes";

type BlueprintListProps = {
  blueprints: Blueprint[];
  onDelete: (blueprint: Blueprint) => void;
};

function formatDate(value: string): string {
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(new Date(value));
}

function formatTargetBuilder(value: TargetBuilder): string {
  return TARGET_BUILDERS.find((builder) => builder.value === value)?.label ?? value;
}

function getStatusLabel(status: BlueprintStatus): string {
  if (status === "generated" || status === "complete") {
    return "Blueprint ready";
  }

  if (status === "confirmed") {
    return "Ready to generate";
  }

  return "Draft";
}

function getStatusClassName(status: BlueprintStatus): string {
  if (status === "generated" || status === "complete") {
    return "bg-emerald-50 text-emerald-700";
  }

  if (status === "confirmed") {
    return "bg-indigo-50 text-indigo-700";
  }

  return "bg-slate-100 text-slate-700";
}

function getActionLabel(status: BlueprintStatus): string {
  if (status === "generated" || status === "complete") {
    return "View blueprint";
  }

  if (status === "confirmed") {
    return "Generate blueprint";
  }

  return "Continue app idea";
}

export function BlueprintList({ blueprints, onDelete }: BlueprintListProps) {
  return (
    <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
      <ul className="divide-y divide-slate-200">
        {blueprints.map((blueprint) => (
          <li key={blueprint.id} className="p-4 sm:p-5">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <Link
                  to={`/blueprints/${blueprint.id}`}
                  className="break-words text-base font-semibold text-slate-950 hover:text-indigo-700"
                >
                  {blueprint.title}
                </Link>
                <div className="mt-2 flex flex-wrap gap-2 text-xs text-slate-500">
                  <span
                    className={`rounded px-2 py-1 font-medium ${getStatusClassName(
                      blueprint.status,
                    )}`}
                  >
                    {getStatusLabel(blueprint.status)}
                  </span>
                  <span>{formatTargetBuilder(blueprint.target_builder)}</span>
                  <span>Updated {formatDate(blueprint.updated_at)}</span>
                </div>
              </div>
              <div className="flex shrink-0 gap-2">
                <Link
                  to={`/blueprints/${blueprint.id}`}
                  className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100"
                >
                  {getActionLabel(blueprint.status)}
                </Link>
                <button
                  type="button"
                  onClick={() => onDelete(blueprint)}
                  className="rounded-md border border-red-200 bg-white px-3 py-2 text-sm font-semibold text-red-700 transition hover:bg-red-50"
                >
                  Delete
                </button>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
